import React, { useState } from 'react'
import CustomRow from '../../../../reusable-components/CustomRow'
import { Button, Col, Space, Table, Tooltip, theme, Tag, Popconfirm, message } from 'antd'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../../../core/utils/reducer'
import CoreEnums from '../../../../../core/utils/enums'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAdd, faEdit, faTrash, faUsers, faChartLine } from '@fortawesome/free-solid-svg-icons'
import CarePlanForm from './CarePlanForm'
import chronicCarePlanIcon from '../../../../../assets/icons/Chronic Care Plan.png'
import dayjs from 'dayjs'
import { deleteCarePlanTemplate } from '../../utils/utils'
import PlanPerformance from './PlanPerformance'

const CarePlansList = ({ carePlansTemplates, dataPoints, handleEnrollmentView, refreshView, isMonitoring }) => {
  const dispatch = useDispatch()
  const [currentPerformancePlan, setCurrentPerformancePlan] = useState(null)

  const openCarePlanForm = (isNew, recordData) => {
    dispatch(
      coreReducer.actions.addTab({
        key: isNew
          ? `${isMonitoring ? CoreEnums.tabKeys.MONITORING_PLANS : CoreEnums.tabKeys.CARE_PLANS}_new`
          : `${isMonitoring ? CoreEnums.tabKeys.MONITORING_PLANS : CoreEnums.tabKeys.CARE_PLANS}_${recordData._id}`,
        closable: true,
        label: 'New Care Plan',
        children: (
          <>
            <CarePlanForm
              isMonitoring={isMonitoring}
              isNew={isNew}
              recordData={recordData}
              dataPoints={dataPoints}
              refreshView={refreshView}
            />
          </>
        )
      })
    )
  }

  const handleDeleteCarePlan = async (record) => {
    try {
      await deleteCarePlanTemplate(record._id)
      message.success('Care plan deleted successfully')
      refreshView() // Refresh the list after deletion
    } catch (error) {
      console.error('Error deleting care plan:', error)
      message.error('Failed to delete care plan')
    }
  }

  const handleViewPerformance = (record) => {
    setCurrentPerformancePlan(record)
  }

  const { token } = theme.useToken()

  // Button style for actions
  const buttonStyle = {
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 16px',
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  }

  // Safely check if an array exists and is actually an array before filtering
  const safeFilter = (array, filterFn) => {
    if (!array || !Array.isArray(array)) return []
    return array.filter(filterFn)
  }

  // Safely get total count with additional type checking
  const getTotalVisits = (tracking) => {
    if (!tracking || !Array.isArray(tracking)) return 0

    return tracking.reduce((sum, item) => {
      // Safety checks for item
      if (!item) return sum
      if (item.trackerType !== 'visits') return sum

      // Safety check for count
      const count = typeof item.count === 'number' ? item.count : 0
      return sum + count
    }, 0)
  }

  if (currentPerformancePlan) {
    return <PlanPerformance plan={currentPerformancePlan} onBack={() => setCurrentPerformancePlan(null)} />
  }

  return (
    <CustomRow>
      <Col span={24}>
        <div style={{ textAlign: 'center', marginBottom: '8px', marginTop: '16px' }}>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 500,
              color: token.colorPrimary,
              margin: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img
              src={chronicCarePlanIcon}
              alt='Chronic Care Plan Icon'
              style={{
                height: '42px',
                marginRight: '15px',
                verticalAlign: 'middle'
              }}
            />
            {isMonitoring ? 'Monitoring Plans' : 'Chronic Care Plans'}
          </h1>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', margin: '16px 0' }}>
          <Button
            style={{
              ...buttonStyle,
              background: '#e6f7ff',
              borderColor: '#91d5ff',
              color: '#1890ff'
            }}
            type='primary'
            onClick={() => openCarePlanForm(true)}
            icon={<FontAwesomeIcon icon={faAdd} style={{ color: '#1890ff' }} />}
          >
            New Care Plan
          </Button>
        </div>

        <Table
          size='small'
          bordered
          dataSource={carePlansTemplates}
          columns={[
            {
              title: 'Name',
              key: 'name',
              dataIndex: 'name',
              sorter: (a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0)
            },
            {
              title: 'Created Date',
              key: 'createdAt',
              dataIndex: 'createdAt',
              render: (text) => (text ? dayjs(text).format('DD-MM-YYYY') : 'N/A'),
              sorter: (a, b) => (a.createdAt && b.createdAt ? dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix() : 0)
            },
            {
              title: 'Last Updated',
              key: 'modifiedAt',
              dataIndex: 'modifiedAt',
              render: (text, record) => {
                // First check modifiedAt since it's the field that actually gets updated
                const dateToUse = text || record.updatedAt || record.createdAt || null
                return dateToUse ? dayjs(dateToUse).format('DD-MM-YYYY') : 'N/A'
              },
              sorter: (a, b) => {
                const dateA = a.modifiedAt || a.updatedAt || a.createdAt || null
                const dateB = b.modifiedAt || b.updatedAt || b.createdAt || null

                if (!dateA && !dateB) return 0
                if (!dateA) return -1
                if (!dateB) return 1

                return dayjs(dateA).unix() - dayjs(dateB).unix()
              }
            },
            {
              title: 'Patients Enrolled',
              key: 'patientsEnrolled',
              dataIndex: 'patientsEnrolled',
              render: (text, record) => {
                // Safety check - make sure patientsEnrolled is an array
                const count =
                  record.patientsEnrolled && Array.isArray(record.patientsEnrolled) ? record.patientsEnrolled.length : 0
                return <Tag color={count > 0 ? 'green' : 'default'}>{count}</Tag>
              },
              sorter: (a, b) => {
                const countA = a.patientsEnrolled && Array.isArray(a.patientsEnrolled) ? a.patientsEnrolled.length : 0
                const countB = b.patientsEnrolled && Array.isArray(b.patientsEnrolled) ? b.patientsEnrolled.length : 0
                return countA - countB
              }
            },
            {
              title: 'Required Visits',
              key: 'requiredVisits',
              dataIndex: 'tracking',
              render: (tracking, record) => {
                const totalVisits = getTotalVisits(tracking)
                return <Tag color={totalVisits > 0 ? 'blue' : 'default'}>{totalVisits}</Tag>
              },
              sorter: (a, b) => {
                const visitsA = getTotalVisits(a.tracking)
                const visitsB = getTotalVisits(b.tracking)
                return visitsA - visitsB
              }
            },
            {
              title: 'Actions',
              render: (record) => {
                // Determine if the plan has enrolled patients
                const hasEnrolledPatients =
                  record.patientsEnrolled &&
                  Array.isArray(record.patientsEnrolled) &&
                  record.patientsEnrolled.length > 0

                return (
                  <Space size='middle' wrap>
                    <Tooltip title='Edit'>
                      <FontAwesomeIcon
                        fontSize={20}
                        size='lg'
                        id='cancel-booking'
                        icon={faEdit}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = token.colorPrimary
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = '#ccc'
                        }}
                        style={{ color: '#ccc', transition: 'color 300ms ease', padding: '0 5px' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          openCarePlanForm(false, record)
                        }}
                      />
                    </Tooltip>
                    <Tooltip title='Enrollment'>
                      <FontAwesomeIcon
                        fontSize={20}
                        size='lg'
                        icon={faUsers}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = token.colorPrimary
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = '#ccc'
                        }}
                        style={{ color: '#ccc', transition: 'color 300ms ease', padding: '0 5px' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEnrollmentView(record)
                        }}
                      />
                    </Tooltip>
                    <Tooltip title='Performance Metrics'>
                      <FontAwesomeIcon
                        fontSize={20}
                        size='lg'
                        icon={faChartLine}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = '#722ed1'
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = '#ccc'
                        }}
                        style={{ color: '#ccc', transition: 'color 300ms ease', padding: '0 5px' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleViewPerformance(record)
                        }}
                      />
                    </Tooltip>
                    <Popconfirm
                      title='Delete Care Plan'
                      description={
                        hasEnrolledPatients
                          ? 'This plan has enrolled patients. Are you sure you want to delete it?'
                          : 'Are you sure you want to delete this care plan?'
                      }
                      onConfirm={() => handleDeleteCarePlan(record)}
                      okText='Yes, Delete'
                      cancelText='Cancel'
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title='Delete'>
                        <FontAwesomeIcon
                          fontSize={20}
                          size='lg'
                          icon={faTrash}
                          onMouseOver={(e) => {
                            e.currentTarget.style.color = '#f5222d'
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.color = '#ccc'
                          }}
                          style={{ color: '#ccc', transition: 'color 300ms ease', padding: '0 5px' }}
                          onClick={(e) => {
                            e.stopPropagation()
                          }}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                )
              }
            }
          ]}
        />
      </Col>
    </CustomRow>
  )
}

export default CarePlansList
